import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DecryptToken } from "../helper/Constants";
// CORRECTED ICON IMPORTS
import { 
  LayoutDashboard, 
  BarChart3, 
  Hash, 
  Users, 
  Target, 
  Megaphone, 
  PhoneCall, 
  Route, 
  ShieldCheck, 
  Settings, 
  LifeBuoy, 
  ChevronDown, 
  Search,
  Circle,
  Ban,
  History,
  Grid3X3 // This replaces 'Dialpad' to fix your error
} from "lucide-react";

const Sidebar = ({ openNav }) => {
  const [selectedTab, setSelectedTab] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userType, setUserType] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const userToken = localStorage.getItem("psx_token");
    const decodedToken = DecryptToken(userToken);
    setUserType(decodedToken?.user_type);
    setSelectedTab(pathname);
  }, [pathname]);

  const handleNavClick = (to) => {
    setSelectedTab(to);
    navigate(to);
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // --- Reusable Navigation Link Component ---
  const NavLink = ({ to, label, icon: Icon, isSubLink = false }) => {
    const isActive = selectedTab === to;
    
    return (
      <div
        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all duration-200 rounded-lg mx-3 my-0.5 group ${
          isActive
            ? "bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 font-semibold border-l-4 border-sky-600"
            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-200"
        }`}
        onClick={() => handleNavClick(to)}
      >
        {Icon && (
            <Icon 
              size={isSubLink ? 14 : 18} 
              className={isActive ? "text-sky-600" : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300"} 
            />
        )}
        <span className={`${isSubLink ? "text-sm" : "text-[15px]"} flex-1`}>{label}</span>
      </div>
    );
  };

  // --- Reusable Sub-Menu Component ---
  const SubNavMenu = ({ title, links, icon: Icon }) => {
    const menuId = title.toLowerCase().replace(/\s+/g, '-');
    const isExpanded = expandedMenus[menuId];
    const hasActiveChild = links.some(link => selectedTab === link.to);

    return (
      <li className="mb-1">
        <div
          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all duration-200 rounded-lg mx-3 ${
            isExpanded || hasActiveChild ? "text-slate-900 dark:text-slate-100" : "text-slate-600 dark:text-slate-400"
          } hover:bg-slate-100 dark:hover:bg-slate-800/50 group`}
          onClick={() => toggleMenu(menuId)}
        >
          {Icon && <Icon size={18} className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />}
          <span className="flex-1 font-medium text-[15px]">{title}</span>
          <ChevronDown 
            size={16} 
            className={`transition-transform duration-300 opacity-50 ${isExpanded ? 'rotate-180' : ''}`} 
          />
        </div>
        <ul
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
          }`}
        >
          {links.map((link, index) => (
            <li key={index} className="pl-6">
              <NavLink to={link.to} label={link.label} icon={link.icon} isSubLink={true} />
            </li>
          ))}
        </ul>
      </li>
    );
  };

  // --- Navigation Data Configuration ---
  
  // Navigation for User Type 3
  const userType3Navigation = useMemo(() => [
    { to: "/calls", label: "Calls", icon: PhoneCall, allowedUserTypes: [3] },
    { to: "/purchase-number-list", label: "Purchase Number List", icon: Hash, allowedUserTypes: [3] },
    { to: "/block-number", label: "Manage Block Numbers", icon: Ban, allowedUserTypes: [3] },
    { to: "/dial-pad", label: "Dial Pad", icon: Grid3X3, allowedUserTypes: [3] }, // FIXED: Using Grid3X3
    { to: "/manage-users", label: "Manage Users", icon: Users, allowedUserTypes: [3] },
    { to: "/ring-group", label: "Ring Group", icon: Target, allowedUserTypes: [3] },
    { to: "/call-history", label: "Call History", icon: History, allowedUserTypes: [3] },
    { to: "/profile", label: "Settings", icon: Settings, allowedUserTypes: [3] },
    { to: "/support", label: "Support", icon: LifeBuoy, allowedUserTypes: [3] }
  ], []);

  // Default Navigation (Other User Types)
  const defaultNavigationItems = useMemo(() => [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, allowedUserTypes: [1, 2, 3, 4, 5] },
    {
      title: "Reporting",
      icon: BarChart3,
      allowedUserTypes: [1, 2, 4, 5],
      links: [
        { to: "/report-list", label: "Report", icon: Circle },
        { to: "/live-calls", label: "Live Calls", icon: Circle },
      ]
    },
    {
      title: "Numbers",
      icon: Hash,
      allowedUserTypes: [1, 2, 3, 4, 5],
      links: [
        { to: "/purchase-number-list", label: "Purchase List", icon: Circle },
        { to: "/block-number", label: "Block Numbers", icon: Circle },
      ]
    },
    { to: "/publishers", label: "Publishers", icon: Users, allowedUserTypes: [1, 2, 4] },
    {
      title: "Targets",
      icon: Target,
      allowedUserTypes: [1, 2, 4],
      links: [
        { to: "/manage-buyers", label: "Manage Buyers", icon: Circle },
        { to: "/create-targets", label: "Create Targets", icon: Circle },
        { to: "/manage-targets", label: "Manage Targets", icon: Circle },
      ]
    },
    {
      title: "Campaigns",
      icon: Megaphone,
      allowedUserTypes: [1, 2, 4],
      links: [
        { to: "/create-campaigns", label: "Create Campaigns", icon: Circle },
        { to: "/manage-campaigns", label: "Manage Campaigns", icon: Circle },
      ]
    },
    {
      title: "Phone System",
      icon: PhoneCall,
      allowedUserTypes: [1, 2, 3],
      links: [
        { to: "/calls", label: "Calls", icon: Circle },
        { to: "/dial-pad", label: "Dial Pad", icon: Circle },
        { to: "/manage-users", label: "Manage Users", icon: Circle },
        { to: "/ring-group", label: "Ring Group", icon: Circle },
        { to: "/call-history", label: "Call History", icon: Circle },
      ]
    },
    { to: "/number-routing", label: "Number Routing", icon: Route, allowedUserTypes: [5] },
    {
      title: "Settings",
      icon: Settings,
      allowedUserTypes: [1, 2, 3, 4, 5],
      links: [{ to: "/profile", label: "Profile", icon: Circle }],
    },
    { to: "/support", label: "Support", icon: LifeBuoy, allowedUserTypes: [1, 2, 3, 4, 5] }
  ], []);

  // --- Filtering Logic ---
  const navigationItems = useMemo(() => 
    userType === 3 ? userType3Navigation : defaultNavigationItems,
    [userType, userType3Navigation, defaultNavigationItems]
  );

  const filteredNavItems = useMemo(() => {
    return navigationItems
      .filter(item => {
        if (!userType) return true;
        if (!item.allowedUserTypes.includes(userType)) return false;
        
        const searchLower = searchQuery.toLowerCase();
        if (item.title) {
          return item.links?.some(link => link.label.toLowerCase().includes(searchLower)) || 
                 item.title.toLowerCase().includes(searchLower);
        }
        return item.label.toLowerCase().includes(searchLower);
      })
      .map(item => {
        if (item.title && searchQuery) {
          const filteredSubLinks = item.links.filter(link =>
            link.label.toLowerCase().includes(searchQuery.toLowerCase())
          );
          return { ...item, links: filteredSubLinks };
        }
        return item;
      });
  }, [navigationItems, userType, searchQuery]);

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 transition-transform duration-300 z-40 w-64 overflow-y-auto custom-scrollbar ${
        openNav ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`}
      style={{ paddingTop: "64px" }}
    >
      {/* Search Bar Section */}
      <div className="p-4 sticky top-0 bg-white dark:bg-slate-950 z-10">
        <div className="relative group">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500 transition-colors" />
          <input
            type="text"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 dark:text-slate-200 transition-all"
          />
        </div>
      </div>

      <nav className="pb-6">
        <ul className="space-y-1">
          {filteredNavItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.title ? (
                <SubNavMenu title={item.title} links={item.links} icon={item.icon} />
              ) : (
                <li>
                  <NavLink to={item.to} label={item.label} icon={item.icon} />
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;